
import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewMerchantAssetInventoryModal = ({
    isOpen,
    onClose,
    onSave,
    defaultMerchantAssetId = '',
    lockAsset = false,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        merchant_asset_id: defaultMerchantAssetId ? String(defaultMerchantAssetId) : '',
        date: '',
        time_slot: '',
        qty_total: '',
        available_units: '',
        total_capacity: '',
        available_capacity: '',
        status: 'Available',
        occupancy_status: 'Empty',
        override_price: '',
        qty_contracted: '',
        qty_freesell: '',
        qty_reserved: '',
        release_date: '',
        blackout: false
    });

    const [formErrors, setFormErrors] = useState({});
    const [merchantAssets, setMerchantAssets] = useState([]);
    const [loadingAssets, setLoadingAssets] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            setFormData((prev) => ({
                ...prev,
                merchant_asset_id: defaultMerchantAssetId
                    ? String(defaultMerchantAssetId)
                    : prev.merchant_asset_id,
            }));
            fetchMerchantAssets();
        }
    }, [isOpen, defaultMerchantAssetId]);

    useEffect(() => {
        const asset = merchantAssets.find(
            (a) => String(a.id) === String(formData.merchant_asset_id)
        );
        setSelectedAsset(asset || null);
    }, [formData.merchant_asset_id, merchantAssets]);

    // Auto-calc capacity when units / asset capacity change
    useEffect(() => {
        const perUnit = Number(selectedAsset?.capacity_per_slot) > 0
            ? Number(selectedAsset.capacity_per_slot)
            : 1;
        const totalUnits = formData.qty_total !== '' ? Number(formData.qty_total) : null;
        const availUnits = formData.available_units !== ''
            ? Number(formData.available_units)
            : totalUnits;

        if (totalUnits != null && !Number.isNaN(totalUnits)) {
            setFormData((prev) => ({
                ...prev,
                total_capacity: String(totalUnits * perUnit),
                available_capacity:
                    availUnits != null && !Number.isNaN(availUnits)
                        ? String(availUnits * perUnit)
                        : prev.available_capacity,
            }));
        }
    }, [formData.qty_total, formData.available_units, selectedAsset]);

    const fetchMerchantAssets = async () => {
        setLoadingAssets(true);
        try {
            const response = await axiosInstance.get('/merchant-assets/', {
                params: { limit: 200 },
            });
            let list = (response.data || []).filter(
                (a) => a.inventory_controlled !== false && a.active !== false
            );
            if (defaultMerchantAssetId) {
                const exists = list.some((a) => String(a.id) === String(defaultMerchantAssetId));
                if (!exists) {
                    const all = response.data || [];
                    const locked = all.find((a) => String(a.id) === String(defaultMerchantAssetId));
                    if (locked) list = [locked, ...list];
                }
            }
            setMerchantAssets(list);
        } catch (error) {
            console.error('Error fetching merchant assets:', error);
        } finally {
            setLoadingAssets(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.merchant_asset_id) errors.merchant_asset_id = 'Asset ID is required';
        if (!formData.date) errors.date = 'Departure date is required';
        const total = Number(formData.qty_total);
        if (!formData.qty_total || Number.isNaN(total) || total <= 0) {
            errors.qty_total = 'Total units must be greater than zero';
        }
        const avail = formData.available_units !== '' ? Number(formData.available_units) : total;
        if (!Number.isNaN(avail) && avail > total) {
            errors.available_units = 'Available units cannot be greater than total units';
        }
        const totalCap = Number(formData.total_capacity);
        const availCap = Number(formData.available_capacity);
        if (formData.total_capacity !== '' && (Number.isNaN(totalCap) || totalCap < 0)) {
            errors.total_capacity = 'Capacity cannot be negative';
        }
        if (
            formData.available_capacity !== ''
            && formData.total_capacity !== ''
            && availCap > totalCap
        ) {
            errors.available_capacity = 'Available capacity cannot be greater than total capacity';
        }
        if (formData.override_price !== '' && Number(formData.override_price) < 0) {
            errors.override_price = 'Override price cannot be negative';
        }
        if (!formData.status) errors.status = 'Status is required';
        if (!formData.occupancy_status) errors.occupancy_status = 'Occupancy status is required';

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            setToast({
                open: true,
                message: Object.values(errors)[0],
                severity: 'error',
            });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                merchant_asset_id: parseInt(formData.merchant_asset_id, 10),
                date: formData.date,
                time_slot: formData.time_slot || null,
                qty_total: parseInt(formData.qty_total, 10),
                available_units: formData.available_units !== ''
                    ? parseInt(formData.available_units, 10)
                    : parseInt(formData.qty_total, 10),
                total_capacity: formData.total_capacity !== ''
                    ? parseInt(formData.total_capacity, 10)
                    : null,
                available_capacity: formData.available_capacity !== ''
                    ? parseInt(formData.available_capacity, 10)
                    : null,
                status: formData.blackout ? 'Unavailable' : formData.status,
                occupancy_status: formData.occupancy_status,
                override_price: formData.override_price !== ''
                    ? parseFloat(formData.override_price)
                    : null,
                qty_contracted: formData.qty_contracted ? parseInt(formData.qty_contracted, 10) : 0,
                qty_freesell: formData.qty_freesell ? parseInt(formData.qty_freesell, 10) : 0,
                qty_reserved: formData.qty_reserved ? parseInt(formData.qty_reserved, 10) : 0,
                release_date: formData.release_date || null,
                blackout: formData.blackout,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                merchant_asset_id: defaultMerchantAssetId ? String(defaultMerchantAssetId) : '',
                date: '',
                time_slot: '',
                qty_total: '',
                available_units: '',
                total_capacity: '',
                available_capacity: '',
                status: 'Available',
                occupancy_status: 'Empty',
                override_price: '',
                qty_contracted: '',
                qty_freesell: '',
                qty_reserved: '',
                release_date: '',
                blackout: false
            });
        } catch (error) {
            console.error('Error saving Merchant Asset Inventory:', error);
            const detail = error.response?.data?.detail;
            setToast({
                open: true,
                message: typeof detail === 'string' ? detail : 'Error saving Merchant Asset Inventory',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add Merchant Asset Inventory</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.merchant_asset_id} required>
                                        <InputLabel>Merchant Asset</InputLabel>
                                        <Select
                                            name="merchant_asset_id"
                                            value={formData.merchant_asset_id}
                                            onChange={handleInputChange}
                                            label="Merchant Asset"

                                            disabled={loadingAssets || lockAsset}
                                        >
                                            <MenuItem value=""><em>Select a Merchant Asset</em></MenuItem>
                                            {merchantAssets.map(ma => (
                                                <MenuItem key={ma.id} value={ma.id}>
                                                    {ma.name} (#{ma.id})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.merchant_asset_id && <MDTypography variant="caption" color="error">{formErrors.merchant_asset_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Departure Date"
                                        name="date"
                                        type="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        error={!!formErrors.date}
                                        helperText={formErrors.date}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Time Slot"
                                        name="time_slot"
                                        type="time"
                                        value={formData.time_slot}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{
                                            step: 60, // 1 minute
                                        }}
                                        helperText="Select time (HH:mm)"
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Total Units"
                                        name="qty_total"
                                        type="number"
                                        value={formData.qty_total}
                                        onChange={handleInputChange}
                                        error={!!formErrors.qty_total}
                                        helperText={formErrors.qty_total}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Available Units"
                                        name="available_units"
                                        type="number"
                                        value={formData.available_units}
                                        onChange={handleInputChange}
                                        error={!!formErrors.available_units}
                                        helperText={formErrors.available_units || 'Defaults to Total Units'}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Total Capacity"
                                        name="total_capacity"
                                        type="number"
                                        value={formData.total_capacity}
                                        onChange={handleInputChange}
                                        error={!!formErrors.total_capacity}
                                        helperText={
                                            formErrors.total_capacity
                                            || (selectedAsset?.capacity_per_slot
                                                ? `Auto: units × ${selectedAsset.capacity_per_slot} capacity/unit`
                                                : 'Required')
                                        }
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Available Capacity"
                                        name="available_capacity"
                                        type="number"
                                        value={formData.available_capacity}
                                        onChange={handleInputChange}
                                        error={!!formErrors.available_capacity}
                                        helperText={formErrors.available_capacity}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.status} required>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Available">Available</MenuItem>
                                            <MenuItem value="Sold Out">Sold Out</MenuItem>
                                            <MenuItem value="Unavailable">Unavailable</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.occupancy_status} required>
                                        <InputLabel>Occupancy Status</InputLabel>
                                        <Select
                                            name="occupancy_status"
                                            value={formData.occupancy_status}
                                            onChange={handleInputChange}
                                            label="Occupancy Status"

                                        >
                                            <MenuItem value="Empty">Empty</MenuItem>
                                            <MenuItem value="Partially Booked">Partially Booked</MenuItem>
                                            <MenuItem value="Fully Booked">Fully Booked</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Override Price"
                                        name="override_price"
                                        type="number"
                                        value={formData.override_price}
                                        onChange={handleInputChange}
                                        error={!!formErrors.override_price}
                                        helperText={formErrors.override_price}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <MDBox mt={2}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.blackout} name="blackout" onChange={handleInputChange} />}
                                            label="Mark Unavailable (Blackout)"
                                        />
                                    </MDBox>
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Inventory'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewMerchantAssetInventoryModal;
